import {
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { SafeUrl } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { concat, interval, Observable, of, Subject, Subscription, timer } from 'rxjs';
import { catchError, distinctUntilChanged, map, startWith, switchMap, take, takeUntil } from 'rxjs/operators';
import * as moment from 'moment';

import { ApplicationMenu } from '../models/menus.model';
import { ParameterData, ParameterSearch } from '../models/parameters.model';
import { Alert, Language } from './global-header.types';
import {
  CORE_LAYOUT_ALERT_SERVICE,
  CORE_LAYOUT_ARKIK_STATUS,
  CORE_LAYOUT_BOM_NOTIFICATIONS,
  CORE_LAYOUT_DISPATCH_SERVICE,
  CORE_LAYOUT_ENVIRONMENT,
  CORE_LAYOUT_PARAMETER_SERVICE,
  CORE_LAYOUT_SVG_ICON_CACHE,
  CORE_LAYOUT_TRACKING_API,
  CORE_LAYOUT_TRANSLATION_SERVICE,
  CoreLayoutEnvironment,
  eEdgeMigrationStatus,
  ICoreLayoutAlertService,
  ICoreLayoutArkikStatusService,
  ICoreLayoutBomNotificationsService,
  ICoreLayoutDispatchService,
  ICoreLayoutParameterService,
  ICoreLayoutSvgIconCacheService,
  ICoreLayoutTrackingApiService,
  ICoreLayoutTranslationService,
} from '../tokens';
import { CoreSidenavAdminService } from '../services/sidenav-admin.service';
import { AuthMsalService } from '../../services/auth-msal.service';

const BOM_TOAST_DURATION_MS = 7000;

interface PopoverLike {
  toggle: () => Promise<void> | void;
  isShown: () => Promise<boolean>;
}

interface BomBellNotification {
  orderCreatedAt: string | Date;
  orderCode: string;
  changedAt: Date;
  orderHdrId: number;
}

@Component({
  selector: 'arkik-global-header',
  templateUrl: './global-header.component.html',
  styleUrls: ['./global-header.component.scss'],
})
export class CoreGlobalHeaderComponent implements OnChanges, OnInit, OnDestroy {
  private readonly _destroy$ = new Subject<void>();
  private _alertsSubscription?: Subscription;

  @Input() isDarkMode = false;
  @Input() isDarkModeEnabled = true;
  @Input() logo = '';
  @Input() logoDark = '';
  @Input() applicationMenus: ApplicationMenu[] = [];
  @Input() FullName = '';
  @Input() imagePath: any;
  @Input() Email = '';
  @Input() languages: Language[] = [];

  @Output() signOut = new EventEmitter<void>();
  @Output() openAppInterface = new EventEmitter<ApplicationMenu>();
  @Output() languageChange = new EventEmitter<string>();
  @Output() darkModeChange = new EventEmitter<boolean>();
  @Output() closeBatchFinalizationModal = new EventEmitter<number>();

  @ViewChild('appsMenu') appsMenu!: PopoverLike;
  @ViewChild('bomBellPopover') bomBellPopover?: PopoverLike;
  @ViewChild('alertWrapper') alertWrapper!: ElementRef<HTMLSpanElement>;

  public appIconSrcByGuid: Record<string, string | SafeUrl | undefined> = {};
  public showUnReadAlerts = true;
  public alerts: Alert[] = [];
  public openBatchFinalizationModal = false;
  private userGuid: string;
  dateFormat = 'hh:mm a';
  locale = 'en-US';
  isDispatch: boolean;
  public isCemexAccount: boolean = false;
  alertsEnabled = false;
  readonly isLocalWeb: boolean = this.env?.isLocalWeb ?? false;
  public offlineLocalSession: boolean;
  public isOnline: boolean;
  public migrationStatus: eEdgeMigrationStatus = eEdgeMigrationStatus.UpToDate;
  public readonly eEdgeMigrationStatus = eEdgeMigrationStatus;
  public alertsRead: Alert[] = [];
  public alertsUnRead: Alert[] = [];
  public isAlertPanelOpen: boolean = false;

  bomBellNotifications$: Observable<BomBellNotification[]> = this.bomNotificationsService
    ? (this.bomNotificationsService.bellNotifications$ as Observable<BomBellNotification[]>)
    : of([]);
  bomBellDotVisible$ = this.bomNotificationsService ? this.bomNotificationsService.bellDotVisible$ : of(false);
  toastNotification$ = (this.bomNotificationsService ? this.bomNotificationsService.toast$ : of(null)).pipe(
    switchMap((n) => concat(of(n), timer(BOM_TOAST_DURATION_MS).pipe(map(() => null)))),
  );

  constructor(
    private cookieService: CookieService,
    private router: Router,
    private uxSideNavAdminService: CoreSidenavAdminService,
    @Optional() @Inject(CORE_LAYOUT_ENVIRONMENT) private env: CoreLayoutEnvironment,
    @Optional() @Inject(CORE_LAYOUT_TRANSLATION_SERVICE) private translationService: ICoreLayoutTranslationService,
    @Optional() @Inject(CORE_LAYOUT_ALERT_SERVICE) private _alertService: ICoreLayoutAlertService,
    @Optional() @Inject(CORE_LAYOUT_ARKIK_STATUS) public arkikStatus: ICoreLayoutArkikStatusService,
    @Optional() @Inject(CORE_LAYOUT_PARAMETER_SERVICE) public parameterService: ICoreLayoutParameterService,
    @Optional() @Inject(CORE_LAYOUT_DISPATCH_SERVICE) private _dispatchService: ICoreLayoutDispatchService,
    @Optional() @Inject(CORE_LAYOUT_TRACKING_API) private _api: ICoreLayoutTrackingApiService,
    @Optional() @Inject(CORE_LAYOUT_SVG_ICON_CACHE) private readonly svgIconCache: ICoreLayoutSvgIconCacheService,
    @Optional()
    @Inject(CORE_LAYOUT_BOM_NOTIFICATIONS)
    private readonly bomNotificationsService: ICoreLayoutBomNotificationsService,
    private authMsalService: AuthMsalService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.applicationMenus) {
      this.resolveAppIcons();
    }
  }

  ngOnInit(): void {
    this.isDispatch = this.router.url.startsWith('/dispatch');
    const userGuidKey = this.env?.UserGuid ?? 'UserGuid';
    this.userGuid = this.cookieService.get(userGuidKey)?.toUpperCase();

    this.parameterService?.setIsCemexParameter();
    this.parameterService?.isCemexAccount$.pipe(takeUntil(this._destroy$)).subscribe((isCemexAccount: boolean) => {
      this.isCemexAccount = isCemexAccount;
    });

    if (this.isDispatch) {
      this.setupDispatchAlerts();
    }

    this.offlineLocalSession = this.isLocalWeb && (this.arkikStatus?.getOfflineToken() ?? '') !== '';
    this.isOnline = this.arkikStatus?.isArkikOnline() ?? true;

    this.arkikStatus?.migrationStatus$.pipe(takeUntil(this._destroy$)).subscribe((status) => {
      this.migrationStatus = status;
      const alertId = 'download-migration';
      this._alertService?.removeAlertById(alertId);
      if (status === eEdgeMigrationStatus.Downloading) {
        this._alertService?.warning(this.pt('views.globals.offline.migration.downloading'), null, true, alertId);
      }
    });

    this.resolveAppIcons();
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  isArkikOnline(): boolean {
    const newConnStatus = this.arkikStatus?.isArkikOnline() ?? true;
    const connAlert = this.isOnline !== newConnStatus;
    this.isOnline = newConnStatus;

    if (connAlert && this.migrationStatus === eEdgeMigrationStatus.UpToDate) {
      if (this.isOnline) {
        this._alertService?.success(this.pt('views.globals.offline-session.reconnected'));
      } else {
        this._alertService?.warning(this.pt('views.globals.online-session.disconnected'));
      }
    }
    return this.isOnline;
  }

  SignOut(): void {
    this.signOut.emit();
    this.authMsalService.logout();
  }

  changeLanguage(e: any): void {
    this.languageChange.emit(e);
  }

  appInterfaceSelected(item: ApplicationMenu): void {
    this.appsMenu.toggle();
    this.openAppInterface.emit(item);
  }

  goToHome(): void {
    this.router.navigate(['/home']);
  }

  toggleDarkMode(event: any): void {
    this.darkModeChange.emit(event.detail);
  }

  sidenavToggleClick(): void {
    this.uxSideNavAdminService.toggleCollapsed();
  }

  get isOrderModule(): boolean {
    return this.router.url.startsWith('/orders');
  }

  private resolveAppIcons(): void {
    const menus = this.applicationMenus ?? [];
    if (!menus.length) {
      this.appIconSrcByGuid = {};
      return;
    }
    for (const menu of menus) {
      const guid = menu.applicationGuid;
      if (!guid || this.appIconSrcByGuid[guid]) continue;
      const icon = (menu.icon ?? '').toString().trim();
      if (!icon) continue;
      if (!this.svgIconCache) {
        this.appIconSrcByGuid = { ...this.appIconSrcByGuid, [guid]: icon };
        continue;
      }
      void this.svgIconCache
        .resolveAssetSrc(icon)
        .then((src) => (this.appIconSrcByGuid = { ...this.appIconSrcByGuid, [guid]: src }))
        .catch(() => (this.appIconSrcByGuid = { ...this.appIconSrcByGuid, [guid]: icon }));
    }
  }

  private setupDispatchAlerts(): void {
    if (!this._dispatchService || !this.parameterService) return;

    this._dispatchService.subgroupsSelected
      .pipe(
        takeUntil(this._destroy$),
        distinctUntilChanged((prev, curr) => prev.length === curr.length && prev.every((v) => curr.includes(v))),
        switchMap((ids) => {
          if (!ids.length) return of([] as ParameterData[]);
          const paramRequest = ids.map<ParameterSearch>((id) => ({
            ParameterCode: 'DISABLE_DISPATCH_ALERTS',
            ParameterScopeTypeId: 2,
            ScopeId: id,
          }));
          return this.parameterService
            .getMultipleParametersByDetails(paramRequest)
            .pipe(catchError(() => of([] as ParameterData[])));
        }),
      )
      .subscribe((p) => {
        this.alertsEnabled = (p?.length ?? 0) > 0 && p.every((x) => x.parameterValue === '1');
        this._manageAlertsPolling();
      });
  }

  private _manageAlertsPolling(): void {
    if (this._alertsSubscription) {
      this._alertsSubscription.unsubscribe();
      this._alertsSubscription = undefined;
    }
    if (!this.alertsEnabled || !this._api) return;

    this._alertsSubscription = interval(60_000)
      .pipe(
        startWith(0),
        switchMap(() => this._api.getAlertsByuser(this.userGuid).pipe(catchError(() => of([] as Alert[])))),
        takeUntil(this._destroy$),
      )
      .subscribe((alerts) => {
        const filters = JSON.parse(localStorage.getItem('selectedFilters') || '{}');
        const plants = filters?.selectedPlants;
        if (plants) {
          this.alerts = alerts.filter((alert) => plants.includes(alert.plantId));
          if (!this.isAlertPanelOpen) {
            this.initAlerts();
          }
        }
      });
  }

  onAlertShow(event: Event): void {
    this.isAlertPanelOpen = (event as CustomEvent).detail;
    if (!this.isAlertPanelOpen) {
      this.initAlerts();
      if (this.alertWrapper) {
        this.alertWrapper.nativeElement.scrollTop = 0;
      }
    }
  }

  onScrollAlerts(event: Event): void {
    const el = event.target as HTMLElement;
    const pct = (el.scrollTop / (el.scrollHeight - el.clientHeight)) * 100;
    if (pct >= 90) {
      if (this.showUnReadAlerts) {
        this.addAlertsUnRead(3);
      } else {
        const rendered = this.alertsRead.length;
        const more = this.alerts.filter((a) => a.isRead).slice(rendered, rendered + 3);
        if (more.length) this.alertsRead = [...this.alertsRead, ...more];
      }
    }
  }

  onTypeChanged(e: Event): void {
    this.showUnReadAlerts = (e as CustomEvent).detail.name === 'unread';
    if (this.alertWrapper) this.alertWrapper.nativeElement.scrollTop = 0;
    this.initAlerts();
  }

  private initAlerts(): void {
    this.alertsRead = this.alerts.filter((a) => a.isRead).slice(0, 8);
    this.alertsUnRead = this.alerts.filter((a) => !a.isRead).slice(0, 8);
  }

  private addAlertsUnRead(qty: number): void {
    const rendered = this.alertsUnRead.length;
    const more = this.alerts.filter((a) => !a.isRead).slice(rendered, rendered + qty);
    if (more.length) this.alertsUnRead = [...this.alertsUnRead, ...more];
  }

  public markAsRead(alertId: number, event: any): void {
    if (!this._api) return;
    const alert = this.alerts.find((a) => a.alertId === alertId);
    event.target.hidden = true;
    this._api
      .markAlertAsRead(this.userGuid, alertId)
      .pipe(take(1))
      .subscribe({
        next: () => {
          if (alert) {
            alert.isRead = true;
            this.alerts = [...this.alerts];
            this.addAlertsUnRead(1);
          }
          this._alertService?.show({
            variant: 'success',
            message: this.pt('views.alerts.mark-as-read-success'),
          });
        },
        error: () => {
          if (alert) alert.isRead = false;
          this._alertService?.show({
            variant: 'error',
            message: this.pt('views.alerts.mark-as-read-fail'),
          });
          event.target.hidden = false;
        },
      });
  }

  markAllAsRead(event: any): void {
    if (!this._api) return;
    event.target.hidden = true;
    this._api
      .markAllAsRead(this.userGuid)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.alerts.forEach((a) => (a.isRead = true));
          this.alerts = [...this.alerts];
          this._alertService?.show({
            variant: 'success',
            message: this.pt('views.alerts.mark-all-read-success'),
          });
        },
        error: () => {
          this._alertService?.show({
            variant: 'error',
            message: this.pt('views.alerts.mark-all-read-fail'),
          });
          event.target.hidden = false;
        },
      });
  }

  onBomNotificationOpen(notification: BomBellNotification): void {
    this.closeBellPopoverIfOpen();
    const createdDate = moment(notification.orderCreatedAt).format('YYYY-MM-DD');
    this.router.navigate(['/orders/backordermanagement'], {
      queryParams: {
        createFrom: createdDate,
        createTo: createdDate,
        backOrderStatus: 'unassigned,assigned',
        // No categoryId: BOM defaults to every category present, so a notification for a
        // scenario outside a hard-coded list (OUTS, SDAY, …) still opens its order.
        assignTo: this.userGuid.toLowerCase(),
        selectedOrderId: notification.orderHdrId,
      },
    });
  }

  private closeBellPopoverIfOpen(): void {
    if (!this.bomBellPopover) return;
    void this.bomBellPopover.isShown().then((shown) => {
      if (shown) void this.bomBellPopover.toggle();
    });
  }

  getTimeAgo(changedAt: Date): string {
    const diffMin = Math.floor((Date.now() - changedAt.getTime()) / 60000);
    if (diffMin < 10) return this.pt('views.orders.back-order-mgmnt.notifications.time-ago.new');
    if (diffMin < 60) return this.pt('views.orders.back-order-mgmnt.notifications.time-ago.minutes', [String(diffMin)]);
    return this.pt('views.orders.back-order-mgmnt.notifications.time-ago.hours', [String(Math.floor(diffMin / 60))]);
  }

  private notificationSeverity(changedAt: Date): 'new' | 'recent' | 'old' {
    const diffMin = Math.floor((Date.now() - changedAt.getTime()) / 60000);
    if (diffMin < 10) return 'new';
    if (diffMin < 45) return 'recent';
    return 'old';
  }

  getNotificationSeverity(changedAt: Date): string {
    return this.notificationSeverity(changedAt);
  }

  getNotificationClockColor(changedAt: Date): string {
    const colors: Record<'new' | 'recent' | 'old', string> = {
      new: 'bright-blue',
      recent: 'bright-orange',
      old: 'true-red',
    };
    return colors[this.notificationSeverity(changedAt)];
  }

  unReadAlertCountString = (alerts: Alert[]): string => {
    const count = this.unReadAlertCount(alerts);
    return count > 9 ? '9+' : count.toString();
  };

  readAlertCount = (alerts: Alert[]): number => alerts.filter((a) => a.isRead).length;

  unReadAlertCount = (alerts: Alert[]): number => alerts.filter((a) => !a.isRead).length;

  getDescription = (alert: Alert): string => {
    const message = this.pt(`views.alerts.desc-${alert.alertTypeId}`);
    const date =
      alert.date != null
        ? new Date(alert.date).toLocaleTimeString(this.locale, { hour12: true, hour: 'numeric', minute: 'numeric' })
        : '';
    return message
      .replace('{TruckCode}', alert.truckCode ?? '')
      .replace('{DeliveryNumber}', alert.deliveryNumber ?? '')
      .replace('{OrderCode}', alert.orderCode ?? '')
      .replace('{Date}', date);
  };

  public closeModalBatchFinalization(): void {
    this.openBatchFinalizationModal = false;
  }

  private pt(key: string, args?: any[]): string {
    return this.translationService ? this.translationService.pt(key, args) : key;
  }
}
